import React, { useState, useEffect } from 'react';
import LeaderboardCard from '../LeaderboardCard';
import { mockPlayers, mockAggregateStats, mockQuestionStats, mockQuiz } from '../../data/carouselMockData';

const PerformanceInsightsSlide: React.FC = () => {
  const [animatedAverage, setAnimatedAverage] = useState(0);
  const topPlayers = mockPlayers.slice(0, 3);

  useEffect(() => {
    // Animate the class average percentage
    let frame = 0;
    const totalFrames = 40;
    const targetValue = mockAggregateStats.class_average_percent;

    const interval = setInterval(() => {
      frame++;
      if (frame <= totalFrames) {
        setAnimatedAverage((targetValue * frame) / totalFrames);
      } else {
        clearInterval(interval);
      }
    }, 30);

    return () => clearInterval(interval);
  }, []);

  // Create question difficulty data
  const questionDifficultyData = mockQuiz.questions.map((q, idx) => {
    const stat = mockQuestionStats[idx];
    return {
      index: idx + 1,
      correctPercent: stat.correct_percentage,
      difficulty: stat.correct_percentage >= 80 ? 'easy' : stat.correct_percentage >= 50 ? 'medium' : 'hard'
    };
  });

  const getDifficultyColor = (percentage: number) => {
    if (percentage >= 80) return '#22c55e'; // green
    if (percentage >= 50) return '#eab308'; // yellow
    return '#ef4444'; // red
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Instant Performance Insights
          </h2>
          <p className="text-xl text-gray-600 mb-6">
            Understand your class performance at a glance
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Class Average Card */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-2xl p-8 text-white">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              <h3 className="text-2xl font-bold">Class Performance</h3>
            </div>
            <div className="text-center py-6">
              <div className="text-7xl font-bold mb-2">
                {animatedAverage.toFixed(1)}%
              </div>
              <div className="text-xl opacity-90">Average Score</div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-blue-400">
              <div className="text-center">
                <div className="text-2xl font-bold">{mockAggregateStats.total_players}</div>
                <div className="text-sm opacity-80">Students</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{mockAggregateStats.std_deviation.toFixed(1)}%</div>
                <div className="text-sm opacity-80">Std. Deviation</div>
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900">Top Performers</h3>
            </div>
            <div className="space-y-3">
              {topPlayers.map((player, index) => (
                <div
                  key={player.id}
                  style={{
                    animation: 'slideUp 0.5s ease-out',
                    animationDelay: `${index * 150}ms`,
                    animationFillMode: 'both'
                  }}
                >
                  <LeaderboardCard
                    player={player}
                    rank={index + 1}
                    variant="podium"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Question Difficulty Heat Map */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Question Difficulty Flow</h3>
          <div className="flex items-center justify-center space-x-2 mb-4">
            {questionDifficultyData.map((q, idx) => (
              <div
                key={q.index}
                className="flex flex-col items-center group relative"
                style={{
                  animation: 'popIn 0.5s ease-out',
                  animationDelay: `${idx * 60}ms`,
                  animationFillMode: 'both'
                }}
              >
                <div
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold text-white cursor-pointer transform transition-transform hover:scale-110 hover:shadow-lg"
                  style={{
                    backgroundColor: getDifficultyColor(q.correctPercent)
                  }}
                >
                  {q.index}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {q.correctPercent.toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center space-x-6 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
              <span>Easy (&gt;80%)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
              <span>Medium (50-80%)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
              <span>Hard (&lt;50%)</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes popIn {
          0% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            transform: scale(1.15);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default PerformanceInsightsSlide;
